import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { StoredSeance } from "apps/idl/src/anakin/models/StoredSeance";
import { ArrayHelper, NumberHelper, DateHelper } from 'apps/idl/src/helpers';
import { Acte } from "apps/idl/src/model/Acte";
import { EMajorationType } from "apps/idl/src/model/EMajorationType";
import { EPlace } from "apps/idl/src/model/EPlace";
import { EStatusSeance } from "apps/idl/src/model/EStatusSeance";
import { IDeplacementByProfession } from "apps/idl/src/model/IDeplacementByProfession";
import { Majoration } from "apps/idl/src/model/Majoration";
import { Traitement } from "apps/idl/src/model/Traitement";
import { IDayRepetition } from "apps/idl/src/modules/event-markers/models/iday-repetition";
import { C_RANGE_REPETITION_TYPE } from "apps/idl/src/modules/event-markers/models/range-repetition";
import { IPatient } from "apps/idl/src/modules/patients/model/IPatient";
import { PatientsService } from "apps/idl/src/modules/patients/services/patients.service";
import { Deplacement } from "apps/idl/src/modules/traitement/model/Deplacement";
import { EIndemniteType } from "apps/idl/src/modules/traitement/model/EIndemniteType";
import { Indemnite } from "apps/idl/src/modules/traitement/model/Indemnite";
import { DestroyableComponentBase } from "apps/idl/src/modules/utils/components/destroyable-component-base";
import { IdlApplicationService } from "apps/idl/src/services/idlApplicationService.service";
import { IndemniteService } from "apps/idl/src/services/indemnite.service";
import { forkJoin, Observable, of } from "rxjs";
import { finalize, map, mergeMap, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { EEtatActe } from "../../enums/EEtatActe";
import { EMoments } from "../../enums/EMoments";
import { DeviceService } from "../../services/device.service";
import { LoaderService } from "../../services/loader.service";
import { SeanceRealisationService } from "../../services/seance-realisation.service";
import { SeanceService } from "../../services/seance.service";
import { EPathologie } from "apps/idl/src/model/EPathologies";
import { ITauxAbattementIK } from "apps/idl/src/anakin/models/IConfigurationTauxAbattementIK";
import { PopoverService } from "../../../shared/services/popover.service";
import { InformationPopoverComponent } from "../information-popover/information-popover.component";
import { Regle } from "apps/idl/src/model/Regle";
import { EEtatRegle } from "apps/idl/src/model/EEtatRegle";

@Component({
	selector: "di-soins-realises",
	templateUrl: "./soins-realises.component.html",
	styleUrls: ["./soins-realises.component.scss"],
})
export class SoinsRealisesComponent extends DestroyableComponentBase implements OnInit, OnDestroy {
	@Input() public seance?: StoredSeance;
	@Input() public patient?: IPatient;
	@Input() public ordonnance?: Traitement;
	@Input() index?: number;
	@Input() updateConcurrentSeances: (seancesConcurrentes: StoredSeance[]) => void;
	@Input() cancel: () => void;

	public isMobile: boolean = false;
	public ikForm: FormGroup;
	public horairePrecise: string = "";
	public total: number;
	public majorationsList: Majoration[] = [];
	public indemnitesForfaitairesList: Indemnite[] = [];
	public selectedMajos: Majoration[] = [];
	public selectedIndemnites: Indemnite[] = [];
	public showAllMajorations = true;
	public showAllDeplacement = true;
	public IKSelected: Indemnite;
	public abattement: number = 0;
	public deplacement: Deplacement;
	public selectedOption: string = 'IK';
	public deplacementByLC: IDeplacementByProfession[];
	public isIkChecked: boolean = false;
	public showCustomAbatement : boolean = false;
	public tauxAbattementIK: ITauxAbattementIK = null;
	public reglesTxAbattementIK: Regle[] = [];

	public concurrentSeances: StoredSeance[] = [];
	public dailySeances: StoredSeance[] = [];
	public displayedActes: Acte[] = [];
	// Seance utilisée pour calculer les abattement d'actes de plusieurs séances
	public seanceActesSelectionnes: StoredSeance;
	// Seance utilisée pour stocker les actes sans abattement
	public seanceActesNonSelectionnes: StoredSeance;
	// True si aucun acte de la séance principale n'est sélectionné
	public confirmDisabled: boolean = true;
	public isLoaded: boolean = false;

	// Seance portant initialement les indemnités et les majorations
	public seanceWithMajorationsIndemnites: StoredSeance;

	public patientDependant = false;
	public seanceDependance = false;
	public acteErreurs: Record<string, string> = {};

	constructor(
		private svcDevice: DeviceService,
		private svcIndemnite: IndemniteService,
		private fb: FormBuilder,
		private svcApplication: IdlApplicationService,
		private svcSeance: SeanceService,
		private svcLoader: LoaderService,
		private svcSeanceRealisation: SeanceRealisationService,
		private svcPatient: PatientsService,
		private svcPopover: PopoverService
	) {
		super();
	}

	ngOnInit() {
		this.svcDevice.isMobile$.pipe(
			takeUntil(this.destroyed$)
		).subscribe((flag: boolean) => {
			this.isMobile = flag;
		});
		this.svcLoader.showLoader("Chargement des soins...");
    	this.initializeForm();
		this.loadData();
		this.patientDependant = this.patient?.pathologies?.includes(EPathologie.dependant);
	}

	private loadData(): void {
		this.initializeDisplayedActes().pipe(
		take(1),
		switchMap(() => forkJoin({
			deplacements: this.svcSeanceRealisation.getDeplacementsByProfession(),
			indemnites: this.svcSeanceRealisation.initializeIndemnitesForfaitairesList(this.seance.startDate),
			majorations: this.svcSeanceRealisation.initializeMajorationsList()
		})),
		tap(({ deplacements, indemnites, majorations }) => {
			this.deplacementByLC = deplacements;
			this.indemnitesForfaitairesList = indemnites;
			this.majorationsList = majorations;
		}),
		mergeMap(() => this.svcSeanceRealisation.getTauxAbattementIK(this.seance.startDate)),
		tap((tauxAbattementIk: ITauxAbattementIK) => {
			this.tauxAbattementIK = tauxAbattementIk;
			this.setReglesTauxAbattementIK();
			this.setDefaultIK();
			this.seanceWithMajorationsIndemnites = this.getSeanceWithIndemnitesMajorations();
			this.initializeSelectedMajorations();
			this.initializeSelectedIndemnities();
			this.calculateTotal();
		}),
		finalize(() => {
			this.svcLoader.hideLoader();
			this.isLoaded = true;
		})
		).subscribe();
	}

	private onChangeTauxAbattementIk(event: string[]):void {
		if(!this.IKSelected) return;
		this.IKSelected.tauxAbattement = event && event.length > 0 ? Number(event[0]) : 0;
		this.calculerMontantIK(this.ikForm.get('distance')?.value);
	}

	private setReglesTauxAbattementIK(): void {
		this.reglesTxAbattementIK = [
			{
				description: "La distance journalière est le cumul des kilomètres facturables, après déduction des 1 et 2 km selon le terrain.",
				etat: EEtatRegle.information
			},
			{
				description: "De 0 à 299 km : 0% d'abattement.",
				etat: EEtatRegle.information
			},
			{
				description: "De 300 à 399 km : 50% d'abattement.",
				etat: EEtatRegle.information
			},
			{
				description: "À partir de 400 km : 100% d'abattement.",
				etat: EEtatRegle.information,
			}
		];
	}

	private showInfoTauxAbattementIK(event: Event) {
		this.svcPopover.open(
			"",
			event.currentTarget,
			InformationPopoverComponent,
			{
				regles: this.reglesTxAbattementIK,
				close: () => {
					this.svcPopover.close();
				},
			}
		)
	}

	private setDefaultIK(): void {
		this.IKSelected = this.svcSeanceRealisation.getIkIndemnite(this.patient);
		this.IKSelected.disabled = false;
		this.selectedOption = this.IKSelected.type;
		this.ikForm.get('distance')?.setValue(this.IKSelected.distance);
		this.ikForm.get('montant')?.setValue(NumberHelper.round(this.IKSelected.price, 2));
		const tauxAbattementValue = this.seance.indemnites?.find(indem => indem.type === EIndemniteType.IK)?.tauxAbattement.toString() ?? "0";
		this.ikForm.get('tauxAbattement')?.setValue([tauxAbattementValue]);
		this.deplacement = this.svcSeanceRealisation.getDeplacement(this.IKSelected);
		this.abattement = this.svcSeanceRealisation.getAbattement(this.deplacement);
	}

	// Tri les actes affichés 
	private sortDisplayedActes(): void {
		let indexAbattement = 1;
		this.displayedActes
			.filter((acte: Acte) => this.seanceActesSelectionnes.actes.some(
				(seanceActe: Acte) => seanceActe.guid === acte.guid
			))
			.sort((a: Acte, b: Acte) => b.price - a.price)
			.forEach((acte: Acte) => {
				acte.indexAbattement = indexAbattement++;
			});

		this.displayedActes.sort((a: Acte, b: Acte) =>
			b.priceCoefficient - a.priceCoefficient ||
			a.guid.localeCompare(b.guid) ||
			a.seanceId.localeCompare(b.seanceId)
		);
	}

	// Initialise les champs du form avec les valeurs par défaut
	private initializeForm(): void {
		let hours: string;
		let minutes: string;
		const moment: IDayRepetition = this.seance.moment;
		if (this.seance.status !== EStatusSeance.done && moment?.type === C_RANGE_REPETITION_TYPE) {
			hours = moment.from.hours.toString();
			// Issue 2708: changement de l'heure du matin de 6h à 8h
			if (hours === "6") hours = "8";
			minutes = moment.from.minutes.toString();
		} else {
			const dateSeance: Date = new Date(this.seance.startDate);
			hours = dateSeance.getHours().toString();
			minutes = dateSeance.getMinutes().toString();
		}
		hours = hours.padStart(2, "0");
		minutes = minutes.padStart(2, "0");
		this.horairePrecise = `${hours}:${minutes}`;
		this.ikForm = this.fb.group({
			distance: [0],
			montant: [0],
			heure: [this.horairePrecise],
			tauxAbattement: ["0"]
		});
	}

	// Récupère les séances concurrentes pour afficher les actes
	public initializeDisplayedActes(): Observable<StoredSeance[]> {
		const startDate = DateHelper.resetDay(new Date(this.seance.startDate));
		return this.svcSeance.selectSeancesByDateAndPatient(
			startDate,
			this.seance.patientId
		).pipe(
			map((seances: StoredSeance[]) => {
				this.dailySeances = seances;
				const targetConcurrentId = this.seance.concurrentSeanceId || undefined;
				const targetMoment = StoredSeance.determineMoment(this.seance.moment ?? startDate) as EMoments;
				seances = seances.filter((seance: StoredSeance) => {
					const seanceMoment = StoredSeance.determineMoment(seance.moment ?? new Date(seance.startDate)) as EMoments;
					return seanceMoment === targetMoment && seance.concurrentSeanceId === targetConcurrentId;
				});

				seances.forEach((seance: StoredSeance) => {
					seance.actes = seance.actes.map((acte: Acte) => {
						const clonedActe = new Acte(acte);
						clonedActe.seanceId = seance._id;
						return clonedActe;
					});
				});
				this.concurrentSeances = seances.filter(seance => seance._id != this.seance._id);
				return seances;
			}),
			switchMap((seances: StoredSeance[]) => {
				this.seanceActesSelectionnes = Object.assign({}, this.seance);
				this.seanceActesNonSelectionnes = Object.assign({}, this.seance);
				this.seanceActesSelectionnes.actes = [];
				this.seanceActesNonSelectionnes.actes = [];
				seances.forEach((seance: StoredSeance) => {
					seance.actes.forEach((acte: Acte) => {
						if ((this.seance.status !== EStatusSeance.done && acte.seanceId === this.seance._id) || acte.etat === EEtatActe.done) {
							this.seanceActesSelectionnes.actes.push(acte);
							acte.etat = EEtatActe.done;
						} else {
							this.seanceActesNonSelectionnes.actes.push(acte);
						}
					})
				})
				this.setSeanceDependance();
				this.setConfirmDisabled();
				return this.initializeAbattement();
			})
		);
	}

	// Calcul l'abattement des actes de chaque séance
	private initializeAbattement(): Observable<StoredSeance[]> {
		this.svcSeanceRealisation.setActesDone([this.seanceActesSelectionnes]);
		const $seances = this.ordonnance.isHADSSIAD 
			|| this.seanceActesSelectionnes.actes.some((acte : Acte) => acte.isAbattementForced) 
				? of([this.seanceActesSelectionnes]) 
				: this.svcSeance.getDailySeancesMapByDate([this.seanceActesSelectionnes], this.patient._id).pipe(
                        switchMap((mapSeancesByDate) => this.svcSeanceRealisation.calculateSeanceAbatement([this.seanceActesSelectionnes], this.patient, mapSeancesByDate))
                    )
		return $seances.pipe(
			tap((seances: StoredSeance[]) => {
				const seance: StoredSeance = ArrayHelper.getFirstElement(seances);
				this.displayedActes = [];
				this.concurrentSeances.forEach((seance: StoredSeance) => {
					seance.actes = [];
				});
				this.seance.actes = [];

				// Met à jour la liste des actes des séances concurrents avec l'abattement
				this.seanceActesSelectionnes = seance;
				[...this.seanceActesSelectionnes.actes, ...this.seanceActesNonSelectionnes.actes].forEach((acte: Acte) => {
					if (acte.seanceId === this.seance._id)
						this.seance.actes.push(acte)
					else
						this.concurrentSeances.find((seance: StoredSeance) => seance._id === acte.seanceId)?.actes.push(acte);
				});

				// Met à jour la liste des actes affichés dans le panneau
				this.displayedActes.push(...this.seanceActesSelectionnes.actes);
				this.displayedActes.push(...this.seanceActesNonSelectionnes.actes);
				if (this.seance.status === EStatusSeance.done) {
					this.displayedActes.forEach((acte: Acte) => {
						if (acte.etat === EEtatActe.not_done)
							acte.etat = EEtatActe.to_be_done;
					});
				}
				this.sortDisplayedActes();
			})
		)
	}

	// True si l'indemnite fait partie de celles sélectionnées
	public isSelectedInListIndem(indem: Indemnite): boolean {
		if (!ArrayHelper.hasElements(this.selectedIndemnites)) return false
		return this.selectedIndemnites.some(deplacement => deplacement.type === indem.type)
	}

	// True si la majoration fait partie de celles sélectionnées
	public isSelectedInListMajo(majo: Majoration): boolean {
		if (!ArrayHelper.hasElements(this.selectedMajos)) return false
		return this.selectedMajos.some(majoration => majoration.type === majo.type)
	}

	// Appelé au clic sur un type d'IK
	selectOption(option: string) {
		// On garde seulement les déplamcents qui ne sont pas de type IK car on va recalculer l'IK puis l'ajouter de nouveau
		this.selectedIndemnites = this.selectedIndemnites.filter((indem: Indemnite) => !indem.isIKType);
		this.IKSelected.type = this.svcIndemnite.getType(option);
		this.selectedOption = option;
		this.deplacement.sectorType = this.svcIndemnite.getSecteur(option);
		this.abattement = this.svcSeanceRealisation.getAbattement(this.deplacement);
		this.IKSelected.id = "IK-" + this.svcApplication.profession + "-" + this.deplacement.sectorType;
		let deplacementLC = this.deplacementByLC.find(deplacement => deplacement.id === this.IKSelected.id);
		this.deplacement.sectorPrice = this.svcIndemnite.getDeplacementTarif(deplacementLC);
		//On récupère également la nouvelle description de l'IK
		this.IKSelected.description = deplacementLC.description;	
		const nbTauxAbattement: number = this.getTauxAbattementValue();
		this.IKSelected.tauxAbattement = nbTauxAbattement;	
		this.svcIndemnite.setIndemnitePrice(this.deplacement, this.IKSelected, this.deplacementByLC, undefined, undefined, nbTauxAbattement);
		this.ikForm.get('montant')?.setValue(NumberHelper.round(this.IKSelected.price, 2));
		this.selectedIndemnites.push(this.IKSelected);
		this.calculateTotal();
	}

	// Appelé lorsqu'on saisi une distance réelle pour l'IK
	public onInputChange(distance: number) {
		// On garde seulement les déplamcents qui ne sont pas de type IK car on va recalculer l'IK puis l'ajouter de nouveau
		this.calculerMontantIK(distance);
	}

	private calculerMontantIK(distance: number) {
		this.selectedIndemnites = this.selectedIndemnites.filter(indem => !indem.isIKType);
		this.deplacement.distance = distance;
		this.IKSelected.distance = distance;
		this.IKSelected.label = `(${distance}km)`;
		this.abattement = this.svcSeanceRealisation.getAbattement(this.deplacement);
		this.deplacement.sectorPrice = this.svcIndemnite.getDeplacementTarif(
			this.deplacementByLC.find(deplacement => deplacement.id === "IK-" + this.svcApplication.profession + "-" + this.deplacement.sectorType)
		);
		const nbTauxAbattement: number = this.getTauxAbattementValue();
		this.svcIndemnite.setIndemnitePrice(this.deplacement, this.IKSelected, this.deplacementByLC, undefined, undefined, nbTauxAbattement);
		this.ikForm.get('montant')?.setValue(NumberHelper.round(this.IKSelected.price, 2));
		this.selectedIndemnites.push(this.IKSelected);
		this.calculateTotal();
	}

	private getTauxAbattementValue(): number {
		return this.ikForm.get('tauxAbattement')?.value ? Number(this.ikForm.get('tauxAbattement')?.value) : 0;
	}

	private getSeanceWithIndemnitesMajorations(): StoredSeance {
		if (this.seance.majorations.length > 0 && this.seance.indemnites.length > 0) {
			return this.seance;
		}

		if (this.concurrentSeances) {
			const found = this.concurrentSeances.find(
				seance => seance.majorations.length > 0 && seance.indemnites.length > 0
			);
			if (found) return found;
		}

		return this.seance;
	}

	public onHoraireChange(event: Event) {
		this.initializeSelectedMajorations();
		this.initializeSelectedIndemnities();
		this.calculateTotal();
	}

	private initializeSelectedMajorations(): void {
		this.selectedMajos = this.svcSeanceRealisation.getSelectedMajorations(this.seanceWithMajorationsIndemnites, this.patient, this.majorationsList, this.horairePrecise, this.seanceActesSelectionnes.actes, this.dailySeances);
	}

	private initializeSelectedIndemnities(): void {
		this.selectedIndemnites = this.svcSeanceRealisation.getSelectedIndemnities(this.seanceWithMajorationsIndemnites, this.patient, this.indemnitesForfaitairesList, this.seanceActesSelectionnes.actes, this.dailySeances);
		const eligibiliteIK = this.svcSeanceRealisation.isIKEligible(this.seanceWithMajorationsIndemnites);
		this.IKSelected.regles = eligibiliteIK.regles;
		this.IKSelected.disabled = !eligibiliteIK.eligible;
		this.isIkChecked = this.selectedIndemnites.some((indemnite: Indemnite) => indemnite.isIKType) && !this.IKSelected.disabled; 
	}

	// Appelé au clic sur la checkbox IK
	toggleIKSelected(): void {
		if(this.IKSelected.disabled) return;
		// Si la config de l'IK était affiché, alors on supprime l'IK de la liste des indemnités sélectionnées
		if (this.isIkChecked) {
			this.selectedIndemnites = this.selectedIndemnites.filter((indem: Indemnite) => !indem.isIKType)
		} else {
			// Sinon on rajoute le dernier type d'IK coché ou IK plaine si il n'y a pas eu d'IK coché précédement
			this.selectedIndemnites.push(this.IKSelected)
		}
		this.isIkChecked = !this.isIkChecked;
		this.calculateTotal();
	}

	toggleMajorations() {
		this.showAllMajorations = !this.showAllMajorations;
	}

	toggleCheckMajoration(event: Event, majo: Majoration) {
		// Vérifier les incompatibilités entre Dim et N1/N2
		const isSundayOrHoliday = majo.type === EMajorationType.SundayAndHolyday;
		const isNightFirstHalf = majo.type === EMajorationType.NightFirstHalf;
		const isNightSecondHalf = majo.type === EMajorationType.NightSecondHalf;

		// Retirer N1 ou N2 si Dim est sélectionné
		if (isSundayOrHoliday && (this.selectedMajos.some(a => a.type === EMajorationType.NightFirstHalf) ||
			this.selectedMajos.some(a => a.type === EMajorationType.NightSecondHalf))) {
			this.selectedMajos = this.selectedMajos.filter(a => ![EMajorationType.NightFirstHalf, EMajorationType.NightSecondHalf].includes(a.type));
		}
		// Retirer Dim si N1 ou N2 est sélectionné
		else if ((isNightFirstHalf || isNightSecondHalf) && this.selectedMajos.some(a => a.type === EMajorationType.SundayAndHolyday)) {
			this.selectedMajos = this.selectedMajos.filter(a => a.type !== EMajorationType.SundayAndHolyday);
		}

		// Gérer les incompatibilités entre MAU, MCI
		const isMau = majo.type === EMajorationType.Mau;
		const isMci = majo.type === EMajorationType.Mci;

		if (isMau) {
			// Désélectionner MIE et MCI si MAU est sélectionné
			this.selectedMajos = this.selectedMajos.filter(a => a.type !== EMajorationType.Mci);
		} else if (isMci) {
			// Désélectionner MAU si MCI est sélectionné
			this.selectedMajos = this.selectedMajos.filter(a => a.type !== EMajorationType.Mau);
		}

		// Toggle la sélection de la majoration
		if (this.selectedMajos?.some(a => a.id === majo.id)) {
			this.selectedMajos = this.selectedMajos.filter(a => a.id !== majo.id); // Retirer si déjà sélectionné
		} else {
			this.selectedMajos.push(majo); // Ajouter si pas sélectionné
		}

		// Calculer le total après modification de la sélection
		this.calculateTotal();
	}

	toggleDeplacement() {
		this.showAllDeplacement = !this.showAllDeplacement;
	}

	toggleCheckDeplacement(event: Event, depla: Indemnite) {
		if (depla.type === EIndemniteType.IFD && this.selectedIndemnites.some(a => a.type === EIndemniteType.IFI)) {
			this.selectedIndemnites = this.selectedIndemnites.filter(a => a.type !== EIndemniteType.IFI);
		}
		else if (depla.type === EIndemniteType.IFI && this.selectedIndemnites.some(a => a.type === EIndemniteType.IFD)) {
			this.selectedIndemnites = this.selectedIndemnites.filter(a => a.type !== EIndemniteType.IFD);
		}
		if (this.selectedIndemnites?.some(a => a.id === depla.id)) {
			this.selectedIndemnites = this.selectedIndemnites.filter(a => a.id !== depla.id);
		} else {
			this.selectedIndemnites.push(depla);
		}
		if (depla.type === EIndemniteType.IFI && this.selectedMajos.some(a => a.type === EMajorationType.Mau)) {
			this.selectedMajos = this.selectedMajos.filter(a => a.type !== EMajorationType.Mau);
		}
		this.calculateTotal();
	}

	updateActesStatus = (actes: Acte[]): void => {
		let allNotDone = true;
		actes.forEach((acte: Acte) => {
			const isDone = this.seanceActesSelectionnes.actes.some(
				selectedActe => selectedActe.guid === acte.guid && selectedActe.seanceId === acte.seanceId
			);
			acte.etat = isDone ? EEtatActe.done : EEtatActe.not_done;
			if (acte.etat !== EEtatActe.not_done) {
				allNotDone = false;
			}
		});
		if (allNotDone && this.concurrentSeances.length > 0) {
			actes.forEach((acte: Acte) => {
				acte.etat = EEtatActe.to_be_done;
			});
		}
	};

	public majSeance(): StoredSeance[] {
		this.updateActesStatus(this.seance.actes);
		this.seance.isSeanceDependance = this.seanceDependance;
		this.concurrentSeances.forEach((seanceChild: StoredSeance) => {
			this.updateActesStatus(seanceChild.actes);
			seanceChild.isSeanceDependance = this.seanceDependance;
		})
		const heureRealisation = this.ikForm.get('heure')?.value;
		const IK: Indemnite = this.selectedIndemnites.find((indemnite: Indemnite) => indemnite.isIKType);
		//Mettre a jour mon patient si IK change seulement si c'est a domicile
		if (IK && this.patient.lastIkUpdate !== IK && this.seance.mePlace == EPlace.home && IK.distance) {
			this.patient.lastIkUpdate = IK;
			this.svcPatient.savePatientAnakin(this.patient).subscribe();
		}
		return this.svcSeanceRealisation.setSeancesDone(this.seance, heureRealisation, this.selectedMajos, this.selectedIndemnites, this.patient, this.concurrentSeances);
	}

	handleClickConfirm() {
		const updatedSeance: StoredSeance[] = this.majSeance();
		if (this.updateConcurrentSeances)
			this.updateConcurrentSeances(updatedSeance)
	}

	handleClickDelete() {
		this.svcSeanceRealisation.annulerSeancesSilent([this.seance, ...this.concurrentSeances]).pipe(
			tap((seancesToUpdate: StoredSeance[]) => {
				if (this.updateConcurrentSeances)
					this.updateConcurrentSeances(seancesToUpdate)
			})
		).subscribe();
	}

	handleClickAnnule() {
		if (this.cancel)
			this.cancel();
	}

	handleCheckboxChange(event: Event, acte: Acte) {
		if(acte.etat === EEtatActe.done){
			acte.etat = EEtatActe.to_be_done;
		}else{
			acte.etat = EEtatActe.done;
		}
		if (this.seanceActesSelectionnes.actes?.some(a => a.guid === acte.guid && a.seanceId === acte.seanceId)) {
			this.seanceActesSelectionnes.actes = this.seanceActesSelectionnes.actes.filter(a => a.guid !== acte.guid || a.seanceId !== acte.seanceId);
			acte.taxAllowance = 1;
			this.seanceActesNonSelectionnes.actes.push(acte);
		} else {
			this.seanceActesSelectionnes.actes.push(acte);
			this.seanceActesNonSelectionnes.actes = this.seanceActesNonSelectionnes.actes.filter(a => a.guid !== acte.guid || a.seanceId !== acte.seanceId);
		}
		this.setSeanceDependance();
		this.setConfirmDisabled();
		this.initializeAbattement().pipe(
			tap(_ => {
				this.initializeSelectedMajorations();
				this.initializeSelectedIndemnities();
				this.calculateTotal();
			})
		).subscribe();
	}

	calculateTotal() {
		const acteTotal = this.seanceActesSelectionnes.actes.reduce((sum, acte) => sum + (acte?.price || 0), 0);
		const majorationTotal = this.selectedMajos.reduce((sum, majo) => sum + (majo?.price || 0), 0);
		const indemnityTotal = this.selectedIndemnites.reduce((sum, indem) => sum + (indem?.price || 0), 0);

		this.total = acteTotal + majorationTotal + indemnityTotal;
	}

	setSeanceDependance(): void {
		this.seanceDependance = this.svcSeanceRealisation.isSeanceDependance(this.seanceActesSelectionnes.actes);
	}

	setConfirmDisabled(): void {
		const actes = this.seanceActesSelectionnes.actes;

		const hasActeForCurrentSeance = actes.some(
			acte => acte.seanceId === this.seance._id
		);
		
		const seanceValidation = this.svcSeanceRealisation.getSeanceValidation(this.seanceActesSelectionnes.actes, this.dailySeances);
		this.acteErreurs = seanceValidation.errors;
		this.confirmDisabled = !hasActeForCurrentSeance || !seanceValidation.isValid;
	}

	public handleTauxActeChange(acteMisAJour: Acte,isAuto :boolean): void {
		this.seance.actes.map(acte => {
			if (acte.guid === acteMisAJour.guid) {
				acte = acteMisAJour as Acte
			}
		});
		if(isAuto) 
		{
			this.showCustomAbatement = false
			this.seance.actes.map(acte => {
				if(acte.isAbattementForced){
					acte.isAbattementForced = false;
					delete acte.taxAllowanceForced;
				}
			})
			this.initializeAbattement()
		}
		else
		{
			this.showCustomAbatement = true
			this.seance.actes.map(acte => {
				if(!acte.isAbattementForced && acte.guid !== acteMisAJour.guid){
					acte.isAbattementForced = true;
					acte.taxAllowanceForced = 1;
				}
			})
		}
		this.seance.actes.map(acte => {
			acte.calculatePrice();
		})
		this.calculateTotal();
	}
}
